import { Controller } from '@hotwired/stimulus'
import { post } from '../lib/request'

const PREVIEW_URL = '/bare/mockup'
const DOWNLOAD_URL = '/admin/mockups_v2/download'
const CANVAS_WIDTH = 720
const TEXT_DEBOUNCE = 250
const LOOKS_DEBOUNCE = 500
const STORAGE_PREFIX = 'mockups-v2:'
const LOOK_PLACEHOLDER_TEXT = 'New prints'
const DESKTOP = '(min-width: 64rem)'

// the fields a look sets, the rest (prints, text, canvas...) are kept
const LOOK_FIELDS = ['frame', 'white_border', 'bg', 'text_style', 'show_banner', 'show_profile']
const CHECKBOXES = ['white_border', 'show_url', 'show_profile', 'show_banner', 'watermark', 'show_qr', 'card_banner']
const RADIOS = ['canvas', 'scene', 'frame', 'bg', 'text_style']

// lives on the admin template's root so the Export button in the mobile nav is in scope
export default class extends Controller {
  static targets = [
    'form',
    'previewArea',
    'stage',
    'iframe',
    'placeholder',
    'loading',
    'product',
    'badge',
    'selectedCount',
    'printsFieldset',
    'frameFieldset',
    'frameNone',
    'sceneFieldset',
    'printsMax',
    'looksSection',
    'bgFieldset',
    'textFieldset',
    'extrasFieldset',
    'whiteBorderToggle',
    'qrToggle',
    'cardBannerToggle',
    'bannerToggle',
    'profileToggle',
    'customBgRadio',
    'customBgSwatch',
    'customBgInput',
    'text',
    'textCount',
    'textCountMobile',
    'look',
    'lookStage',
    'lookFrame',
    'tool',
    'exports',
    'exportsToggle',
    'exportsBackdrop',
    'exportsBadge',
    'exportsList',
    'exportsEmpty',
    'toast',
    'resume',
  ]

  connect() {
    if (!this.hasFormTarget) return

    const { userId, maxProducts, canvasHeights } = this.formTarget.dataset
    this.storageKey = `${STORAGE_PREFIX}${userId}`
    this.maxProducts = Number(maxProducts)
    this.canvasHeights = JSON.parse(canvasHeights)

    // ids in the order they were picked, which is the order they're laid out
    this.selectedIds = []
    this.frontIndex = 0
    this.currentUrl = null
    this.looksKey = null

    // as the page rendered it, their Design settings, what Start fresh goes back to
    this.defaults = this.state
    this.restore()
    if (this.worked) this.resumeTarget.showModal()

    this.resizeObserver = new ResizeObserver(() => {
      this.fitStage()
      this.fitLooks()
    })
    this.resizeObserver.observe(this.previewAreaTarget)
    this.resizeObserver.observe(this.formTarget)

    // everything scrolls inside the editor, the document itself never should.
    // mobile browsers scroll it anyway to bring a focused input above the
    // keyboard, then leave it there, so put it back
    window.visualViewport?.addEventListener('resize', this.keyboardClosed)
    this.formTarget.addEventListener('focusout', this.resetDocumentScroll)
    document.addEventListener('click', this.closeExportsOutside)

    // exports being created: their timers, the badge, and noticing new rows
    // (prepended by the download stream, replaced by the broadcast when done)
    this.shareFiles = new Map()
    this.exportsObserver = new MutationObserver(() => this.tickExports())
    this.exportsObserver.observe(this.exportsTarget, { childList: true, subtree: true })
    this.exportsTimer = setInterval(() => this.tickExports(), 1000)
    this.tickExports()

    this.refresh()
  }

  disconnect() {
    window.visualViewport?.removeEventListener('resize', this.keyboardClosed)
    this.formTarget?.removeEventListener('focusout', this.resetDocumentScroll)
    document.removeEventListener('click', this.closeExportsOutside)
    this.exportsObserver?.disconnect()
    clearInterval(this.exportsTimer)
    this.resizeObserver?.disconnect()
    clearTimeout(this.textTimeout)
    clearTimeout(this.looksTimeout)
    clearTimeout(this.toastTimeout)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  // state

  // read straight off the inputs, disabled or not, the server decides what a
  // canvas can show so the choices survive switching back and forth
  get state() {
    const form = this.formTarget
    const state = { product_ids: this.selectedIds.join(','), text: this.textTarget.value.trim() }

    RADIOS.forEach((name) => {
      state[name] = form.querySelector(`input[name="${name}"]:checked`)?.value || ''
    })
    CHECKBOXES.forEach((name) => {
      state[name] = form.querySelector(`input[name="${name}"]`).checked.toString()
    })

    return state
  }

  get params() {
    return { v: '2', user_id: this.formTarget.dataset.userId, ...this.state }
  }

  setInputs(values) {
    const form = this.formTarget

    Object.entries(values).forEach(([name, value]) => {
      if (CHECKBOXES.includes(name)) {
        form.querySelector(`input[name="${name}"]`).checked = value === true || value === 'true'
      }
      else if (name === 'bg') {
        this.setBackground(value)
      }
      else if (RADIOS.includes(name)) {
        const input = form.querySelector(`input[name="${name}"][value="${value}"]`)
        if (input) input.checked = true
      }
      else if (name === 'text') {
        this.textTarget.value = value
      }
    })
  }

  setBackground(hex) {
    const preset = this.formTarget.querySelector(`input[name="bg"][value="${hex}"]`)
    if (preset && preset !== this.customBgRadioTarget) {
      preset.checked = true
      return
    }

    this.customBgRadioTarget.value = hex
    this.customBgRadioTarget.checked = true
    this.customBgSwatchTarget.style.background = `#${hex}`
    this.customBgInputTarget.value = `#${hex}`
  }

  // the last used settings, per user, so coming back picks up where you left off
  restore() {
    let saved
    try {
      saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null')
    }
    catch {
      return
    }
    if (!saved) return

    const available = new Set(this.productTargets.map(button => button.dataset.id))
    this.selectedIds = (saved.product_ids || '').split(',').filter(id => available.has(id)).slice(0, this.maxProducts)

    this.setInputs(saved)
  }

  // anything saved that isn't just the defaults
  get worked() {
    return JSON.stringify(this.state) !== JSON.stringify(this.defaults)
  }

  // escape closes it too, which also keeps their work
  continueEditing() {
    this.resumeTarget.close()
  }

  // a clean slate: the defaults again and nothing saved
  startOver() {
    this.selectedIds = []
    this.setInputs(this.defaults)
    this.refresh()
    this.forget()
    this.resumeTarget.close()
  }

  forget() {
    try {
      window.localStorage.removeItem(this.storageKey)
    }
    catch {
      // nothing saved to forget
    }
  }

  persist() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.state))
    }
    catch {
      // private mode or storage full, it's only a convenience
    }
  }

  // inputs

  toggleProduct({ currentTarget }) {
    const id = currentTarget.dataset.id

    if (this.selectedIds.includes(id)) {
      this.selectedIds = this.selectedIds.filter(selected => selected !== id)
    }
    // a scene has one print, picking another swaps it
    else if (this.scene) {
      this.selectedIds = [id]
    }
    else if (this.selectedIds.length >= this.maxProducts) {
      this.showToast(`Up to ${this.maxProducts} prints`)
      return
    }
    else {
      this.selectedIds.push(id)
    }

    this.refresh()
  }

  customBackground({ target }) {
    this.setBackground(target.value.replace('#', ''))
    this.refresh()
  }

  textChanged() {
    this.syncTextCount()

    clearTimeout(this.textTimeout)
    this.textTimeout = setTimeout(() => this.refresh(), TEXT_DEBOUNCE)
  }

  applyLook({ currentTarget }) {
    this.setInputs(JSON.parse(currentTarget.dataset.look))
    this.refresh()
  }

  selectTool({ currentTarget }) {
    if (currentTarget.disabled) return

    this.activateTool(currentTarget.dataset.tool)
  }

  activateTool(tool) {
    this.formTarget.dataset.tool = tool
    this.toolTargets.forEach((button) => {
      button.setAttribute('aria-current', (button.dataset.tool === tool).toString())
    })
    this.formTarget.querySelectorAll('[data-tool-section]').forEach((section) => {
      section.toggleAttribute('data-active', section.dataset.toolSection === tool)
    })
  }

  // preview

  refresh() {
    this.syncUi()
    this.persist()

    const state = this.state
    const ready = this.selectedIds.length > 0 || this.profileCard
    this.placeholderTarget.classList.toggle('hidden', ready)

    if (!ready) {
      this.currentUrl = null
      this.iframeTargets.forEach(iframe => iframe.classList.add('invisible'))
    }
    else {
      const url = `${PREVIEW_URL}?${new URLSearchParams(this.params).toString()}`
      if (url !== this.currentUrl) {
        this.currentUrl = url
        this.loadingTarget.classList.remove('hidden')

        // load into the hidden iframe and swap once painted, avoids flashing white
        this.backIframe.dataset.url = url
        this.backIframe.src = url
      }
    }

    this.scheduleLooks(state)
  }

  iframeLoaded({ target }) {
    if (!target.dataset.url || target.dataset.url !== this.currentUrl) return

    target.classList.remove('invisible')
    this.frontIframe.classList.add('invisible')
    this.frontIndex = this.iframeTargets.indexOf(target)
    this.loadingTarget.classList.add('hidden')
  }

  get canvasHeight() {
    return this.canvasHeights[this.state.canvas] || this.canvasHeights.story
  }

  // the original mockups: one framed print on a texture or in a room
  get scene() {
    return this.state.canvas === 'scene'
  }

  // a profile card has the banner and photo & name instead of prints
  get profileCard() {
    return this.state.canvas === 'card'
  }

  // scale the 720px wide iframes down with css instead of reloading the page,
  // so the preview is the exact page the screenshot is taken of
  fitStage() {
    const area = this.previewAreaTarget
    const styles = getComputedStyle(area)
    const width = area.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
    const height = area.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom)
    const scale = Math.max(Math.min(width / CANVAS_WIDTH, height / this.canvasHeight), 0.05)

    this.stageTarget.style.width = `${CANVAS_WIDTH * scale}px`
    this.stageTarget.style.height = `${this.canvasHeight * scale}px`
    this.iframeTargets.forEach((iframe) => {
      iframe.height = this.canvasHeight
      iframe.style.transform = `scale(${scale})`
    })
  }

  // looks, desktop only

  // the thumbnails only change when what a look keeps changes, so they're
  // reloaded for prints, text and canvas, not when a look is applied
  scheduleLooks(state) {
    this.lookTargets.forEach((button) => {
      const look = JSON.parse(button.dataset.look)
      const selected = LOOK_FIELDS.every(field => String(look[field]) === String(state[field]))
      button.toggleAttribute('data-selected', selected)
    })

    if (!window.matchMedia(DESKTOP).matches || this.scene) return

    const kept = Object.fromEntries(Object.entries(state).filter(([field]) => !LOOK_FIELDS.includes(field)))
    const key = JSON.stringify(kept)
    if (key === this.looksKey) return

    this.looksKey = key
    clearTimeout(this.looksTimeout)
    this.looksTimeout = setTimeout(() => this.loadLooks(kept), LOOKS_DEBOUNCE)
  }

  loadLooks(kept) {
    this.fitLooks()

    const ready = this.selectedIds.length > 0 || this.profileCard
    this.lookTargets.forEach((button, index) => {
      const frame = this.lookFrameTargets[index]
      if (!ready) {
        frame.src = 'about:blank'
        return
      }

      const look = JSON.parse(button.dataset.look)
      const params = { v: '2', user_id: this.formTarget.dataset.userId, ...kept, ...look }
      params.text = kept.text || LOOK_PLACEHOLDER_TEXT
      frame.src = `${PREVIEW_URL}?${new URLSearchParams(params).toString()}`
    })
  }

  fitLooks() {
    this.lookStageTargets.forEach((stage, index) => {
      const width = stage.clientWidth
      if (!width) return

      const scale = width / CANVAS_WIDTH
      stage.style.height = `${this.canvasHeight * scale}px`
      this.lookFrameTargets[index].height = this.canvasHeight
      this.lookFrameTargets[index].style.transform = `scale(${scale})`
    })
  }

  // export

  async download() {
    if (!this.hasFormTarget) return
    if (this.selectedIds.length === 0 && !this.profileCard) {
      this.showToast('Pick a print first')
      return
    }
    if (this.exporting) return

    this.exporting = true
    this.setExportsOpen(true)

    try {
      await post(DOWNLOAD_URL, { body: JSON.stringify(this.state), responseKind: 'turbo-stream' })
    }
    finally {
      // brief cooldown so a double tap doesn't queue two screenshots
      setTimeout(() => { this.exporting = false }, 1500)
    }
  }

  toggleExports() {
    this.setExportsOpen(this.exportsTarget.classList.contains('hidden'))
  }

  closeExports() {
    this.setExportsOpen(false)
  }

  setExportsOpen(open) {
    this.exportsTarget.classList.toggle('hidden', !open)
    this.exportsTarget.classList.toggle('flex', open)
    this.exportsBackdropTarget.classList.toggle('hidden', !open)
    this.exportsToggleTarget.setAttribute('aria-expanded', open.toString())

    if (open) this.prefetchShareFiles()
  }

  // the backdrop closes the sheet on a phone, on desktop a click elsewhere does
  closeExportsOutside = (event) => {
    if (this.exportsTarget.classList.contains('hidden')) return
    if (this.exportsTarget.contains(event.target) || this.exportsToggleTarget.contains(event.target)) return
    if (this.exportsBackdropTarget.contains(event.target)) return
    if (event.target.closest('[data-action*="mockups-v2#download"]')) return

    this.setExportsOpen(false)
  }

  tickExports() {
    const generating = this.exportsTarget.querySelectorAll('[data-export-generating]')

    // only write what changed, the drawer's observer calls this on every change
    generating.forEach((row) => {
      const seconds = Math.max(0, Math.floor((Date.now() - Number(row.dataset.exportStartedAt)) / 1000))
      const elapsed = row.querySelector('[data-export-elapsed]')
      const text = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
      if (elapsed && elapsed.textContent !== text) elapsed.textContent = text
    })

    if (this.exportsBadgeTarget.textContent !== String(generating.length)) {
      this.exportsBadgeTarget.textContent = generating.length
    }
    this.exportsBadgeTarget.classList.toggle('hidden', generating.length === 0)
    this.exportsBadgeTarget.classList.toggle('flex', generating.length > 0)

    if (this.hasExportsEmptyTarget) {
      this.exportsEmptyTarget.classList.toggle('hidden', this.exportsListTarget.children.length > 0)
    }
  }

  // phones

  // on a phone a finished export goes to the share sheet (save to photos,
  // instagram...) instead of the files app. browsers only allow sharing right
  // after a tap, so the latest files are fetched when the drawer opens
  get canShareFiles() {
    return window.matchMedia('(pointer: coarse)').matches && typeof navigator.canShare === 'function'
  }

  prefetchShareFiles() {
    if (!this.canShareFiles) return

    const links = [...this.exportsTarget.querySelectorAll('a[data-export-share]')]
    const urls = [...new Set(links.map(link => link.dataset.exportShare))].slice(0, 6)
    urls.forEach((url) => {
      const link = links.find(candidate => candidate.dataset.exportShare === url)
      this.shareFile(url, link.dataset.exportFilename).catch(() => {})
    })
  }

  shareFile(url, filename) {
    if (!this.shareFiles.has(url)) {
      const file = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`${response.status}`)
          return response.blob()
        })
        .then(blob => new File([blob], filename, { type: blob.type || 'image/png' }))
      file.catch(() => this.shareFiles.delete(url))
      this.shareFiles.set(url, file)
    }

    return this.shareFiles.get(url)
  }

  async shareExport(event) {
    if (!this.canShareFiles) return

    const link = event.currentTarget
    event.preventDefault()

    try {
      const file = await this.shareFile(link.dataset.exportShare, link.dataset.exportFilename)
      if (!navigator.canShare({ files: [file] })) throw new Error('unsupported')

      await navigator.share({ files: [file] })
    }
    catch (error) {
      // closing the share sheet isn't an error, anything else falls back to a download
      if (error?.name !== 'AbortError') window.location.href = link.href
    }
  }

  // ui

  syncUi() {
    const profileCard = this.profileCard
    const scene = this.scene

    // a scene is one framed print, as in the original mockups
    if (scene && this.state.frame === 'none') this.setInputs({ frame: 'black' })
    if (scene && this.selectedIds.length > 1) this.selectedIds = this.selectedIds.slice(0, 1)

    const count = this.selectedIds.length
    this.productTargets.forEach((button, index) => {
      const position = this.selectedIds.indexOf(button.dataset.id)
      const badge = this.badgeTargets[index]

      button.setAttribute('aria-pressed', (position !== -1).toString())
      badge.textContent = position + 1
      badge.classList.toggle('hidden', position === -1 || scene)
      badge.classList.toggle('flex', position !== -1 && !scene)
    })
    this.selectedCountTarget.textContent = count
    this.printsMaxTarget.textContent = scene ? 1 : this.maxProducts
    this.placeholderTarget.textContent = scene ? 'Pick a print to start' : `Pick up to ${this.maxProducts} prints to start`

    // what doesn't apply to the canvas isn't shown at all, rather than greyed out
    this.printsFieldsetTarget.hidden = profileCard
    this.frameFieldsetTarget.hidden = profileCard
    this.frameNoneTarget.closest('label').hidden = scene
    // white border is the gallery's other variant, frames come with a mat
    this.whiteBorderToggleTarget.closest('label').hidden = scene || this.state.frame !== 'none'
    this.sceneFieldsetTarget.hidden = !scene
    this.looksSectionTarget.hidden = scene
    this.bgFieldsetTarget.hidden = scene
    this.textFieldsetTarget.hidden = scene
    this.extrasFieldsetTarget.hidden = scene

    // a profile card always has the photo & name and its own banner toggle,
    // and banners only show when there is one
    this.showLabel(this.bannerToggleTarget, !profileCard && !this.bannerToggleTarget.disabled)
    this.showLabel(this.cardBannerToggleTarget, profileCard && !this.cardBannerToggleTarget.disabled)
    this.showLabel(this.profileToggleTarget, !profileCard)
    this.showLabel(this.qrToggleTarget, profileCard)

    const unavailable = {
      prints: profileCard,
      frame: profileCard,
      scene: !scene,
      bg: scene,
      text: scene,
      extras: scene,
    }
    this.toolTargets.forEach((button) => {
      button.hidden = !!unavailable[button.dataset.tool]
    })
    if (unavailable[this.formTarget.dataset.tool]) this.activateTool(scene ? 'scene' : 'text')
    // straight to the backgrounds when switching to a scene
    if (scene && this.lastCanvas !== 'scene' && this.lastCanvas !== undefined) this.activateTool('scene')
    this.lastCanvas = this.state.canvas

    this.syncTextCount()
    this.fitStage()
  }

  showLabel(input, shown) {
    const label = input.closest('label')
    label.classList.toggle('hidden', !shown)
    label.classList.toggle('flex', shown)
  }

  syncTextCount() {
    const length = this.textTarget.value.length
    this.textCountTarget.textContent = length
    this.textCountMobileTarget.textContent = length
  }

  showToast(message) {
    this.toastTarget.textContent = message
    this.toastTarget.classList.remove('hidden')

    clearTimeout(this.toastTimeout)
    this.toastTimeout = setTimeout(() => this.toastTarget.classList.add('hidden'), 2000)
  }

  // only once the viewport is full height again, resetting with the keyboard
  // open would push the focused input under it
  keyboardClosed = () => {
    if (window.visualViewport.height >= window.innerHeight - 1) this.resetDocumentScroll()
  }

  resetDocumentScroll = () => {
    requestAnimationFrame(() => {
      if (window.scrollY !== 0 || document.documentElement.scrollTop !== 0) window.scrollTo(0, 0)
    })
  }

  get frontIframe() {
    return this.iframeTargets[this.frontIndex]
  }

  get backIframe() {
    return this.iframeTargets[1 - this.frontIndex]
  }
}
